import React, { useState, useEffect } from "react";
import {
  Box,
  Text,
  Button,
  Flex,
  useToast,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  useColorMode,
  ModalFooter,
  Select,
} from "@chakra-ui/react";
import { cqAPI, getBid } from "../../lib/Auth";

const PlanComponent = ({ plan, businessId, onParticipate, onActivate, onDeactivate }) => {
  const isPlanObsolete = plan.obsolete;
  const isPlanActive = plan.active;
  const isSharedPlan = Boolean(plan.metadata.shared_plan_template);

  return (
    <Flex
      direction="column"
      alignItems="flex-start"
      borderBottom="1px solid #888"
      p={4}
      mb={4}
      textAlign="left"
    >
      <Box mb={2}>
        <Text fontWeight="bold">
          {plan.metadata.visible_name || plan.nickname}
        </Text>
        <Text>(${(plan.amount / 100).toFixed(2)} per {plan.interval})</Text>
      </Box>

      <Text fontWeight="bold" mb={2}>
        What is covered:
      </Text>
      <Box mb={4} textAlign="left">
        {Object.entries(JSON.parse(plan.metadata.coverage)).map(
          ([key, value], i) => (
            <Text key={i} textAlign="left">
              {key.charAt(0).toUpperCase() + key.slice(1)}: {value}%
            </Text>
          )
        )}
      </Box>

      {plan.metadata.creditback && (
        <Box mb={4} textAlign="left">
          <Text>Credit back: ${plan.metadata.creditback}</Text>
        </Box>
      )}

      <Box mt={2}>
        {isPlanObsolete ? (
          <>
            <Text mb={4}>
              This plan is not being shared/marketed anymore.
            </Text>
            <Button colorScheme="yellow" onClick={() => onDeactivate(plan.id)}>Stop Participation</Button>
          </>
        ) : isPlanActive ? (
          <>
            {isSharedPlan && (
              <Button
                as="a"
                href={`/signup?bid=${businessId}&plan=${plan.id}`}
                target="_blank"
                mb={4}
                colorScheme="blue"
              >
                Signup link for website
              </Button>
            )}
            <Button colorScheme="yellow" onClick={() => onDeactivate(plan.id)}>Stop Participation</Button>
          </>
        ) : (
          <Button colorScheme="green" onClick={() => (isSharedPlan ? onParticipate(plan.id) : onActivate(plan.id))}>
            {isSharedPlan ? "Participate" : "Activate"}
          </Button>
        )}
      </Box>
    </Flex>
  );
};

const PlanAdmin = () => {
  const [ownPlans, setOwnPlans] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { colorMode } = useColorMode();
  const [newPlan, setNewPlan] = useState({
    visible_name: "",
    term: "",
    description: "",
    amount: "",
    interval: "month",
    coverage: {},
    note: "",
  });
  const toast = useToast();

  const businessId = getBid();

  const fetchPlans = async () => {
    const response = await cqAPI.get(`/settings/discountplan/admin`);
    const plans = response.data;
    setOwnPlans(plans.own || []);
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const handleParticipate = async (planId) => {
    try {
      const response = await cqAPI.post(`/settings/discountplan/participate`, { plan_id: planId });
      if (response.type === "error") {
        toast({
          title: "Error",
          description: response.msg,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } else {
        fetchPlans();
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "An error occurred while participating in the plan.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleActivate = async (planId) => {
    try {
      const response = await cqAPI.post(`/settings/discountplan/activate`, { plan_id: planId });
      if (response.type === "error") {
        toast({
          title: "Error",
          description: response.msg,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } else {
        fetchPlans();
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "An error occurred while activating the plan.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDeactivate = async (planId) => {
    try {
      const response = await cqAPI.post(`/settings/discountplan/deactivate`, { plan_id: planId });
      if (response.type === "error") {
        toast({
          title: "Error",
          description: response.msg,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } else {
        fetchPlans();
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "An error occurred while deactivating the plan.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleAddCategory = () => {
    setNewPlan((prevState) => ({
      ...prevState,
      coverage: {
        ...prevState.coverage,
        [""]: "",
      },
    }));
  };

  const handleCategoryChange = (index, field, value) => {
    const updatedCoverage = { ...newPlan.coverage };
    const categoryNames = Object.keys(updatedCoverage);
    if (field === "category_name") {
      const currentValue = updatedCoverage[categoryNames[index]];
      delete updatedCoverage[categoryNames[index]];
      updatedCoverage[value] = currentValue;
    } else if (field === "covered_amount") {
      updatedCoverage[categoryNames[index]] = value;
    }
    setNewPlan({ ...newPlan, coverage: updatedCoverage });
  };

  const handleNewPlanChange = (field, value) => {
    setNewPlan({ ...newPlan, [field]: value });
  };

  const handleNewPlanSubmit = async () => {
    const { visible_name, term, description, amount, coverage } = newPlan;
    if (
      !visible_name ||
      !term ||
      !description ||
      !amount ||
      Object.keys(coverage).some((key) => !key || !coverage[key])
    ) {
      toast({
        title: "Error",
        description: "Please fill out all fields, including category name and coverage percentage.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      const response = await cqAPI.post(`/settings/discountplan/admin`, newPlan);
      if (response.type === "error") {
        toast({
          title: "Error",
          description: response.msg,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Success",
          description: "New plan created successfully.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        setIsModalOpen(false);
        setNewPlan({
          visible_name: "",
          term: "",
          description: "",
          amount: "",
          interval: "month",
          coverage: {},
          note: "",
        });
        fetchPlans();
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "An error occurred while creating the new plan.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Box
      p={4}
      overflowY="auto"
      maxHeight="100vh"
      width="100%"
      position="relative"
    >
      <Flex
        p={4}
        overflowY="auto"
        maxHeight="100vh"
        padding="8px 24px 24px 24px"
        height="95vh"
        bgColor={colorMode === "light" ? "#FFFFFF" : "#212330"}
        borderRadius="16px"
        flexDirection="column"
      >
        <Flex justify="flex-end" mb={4}>
          <Button colorScheme="red" onClick={() => setIsModalOpen(true)}>
            New Plan
          </Button>
        </Flex>
        {ownPlans.map((plan, index) => (
          <PlanComponent
            key={index}
            plan={plan}
            businessId={businessId}
            onParticipate={handleParticipate}
            onActivate={handleActivate}
            onDeactivate={handleDeactivate}
          />
        ))}

        {/* New Plan Modal */}
        <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
          <ModalOverlay />
          <ModalContent maxW="400px">
            <ModalHeader>New Plan</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Box mb={4}>
                <Input
                  placeholder="Name"
                  value={newPlan.visible_name}
                  onChange={(e) => handleNewPlanChange("visible_name", e.target.value)}
                />
              </Box>
              <Box mb={4}>
                <Input
                  type="number"
                  placeholder="Commitment period in months"
                  value={newPlan.term}
                  onChange={(e) => handleNewPlanChange("term", e.target.value)}
                />
              </Box>
              <Box mb={4}>
                <Input
                  placeholder="Description"
                  value={newPlan.description}
                  onChange={(e) => handleNewPlanChange("description", e.target.value)}
                />
              </Box>
              <Box mb={4}>
                <Input
                  type="number"
                  placeholder="Price"
                  value={newPlan.amount}
                  onChange={(e) => handleNewPlanChange("amount", e.target.value)}
                />
              </Box>
              <Box mb={4}>
                <Select
                  value={newPlan.interval}
                  onChange={(e) => handleNewPlanChange("interval", e.target.value)}
                >
                  <option value="month">Charge Monthly</option>
                  <option value="year">Charge Annually</option>
                </Select>
              </Box>
              {Object.entries(newPlan.coverage).map(([category, percentage], index) => (
                <Flex key={index} mb={2}>
                  <Input
                    placeholder="Category Name"
                    value={category}
                    onChange={(e) =>
                      handleCategoryChange(index, "category_name", e.target.value)
                    }
                    flex="1"
                    mr={2}
                  />
                  <Input
                    type="number"
                    placeholder="%"
                    value={percentage}
                    onChange={(e) =>
                      handleCategoryChange(index, "covered_amount", e.target.value)
                    }
                    width="60px"
                  />
                </Flex>
              ))}
              <Button onClick={handleAddCategory} colorScheme="blue" mb={4}>
                Add a new category
              </Button>
              <Box mb={4}>
                <Input
                  placeholder="Note"
                  value={newPlan.note}
                  onChange={(e) => handleNewPlanChange("note", e.target.value)}
                />
              </Box>
            </ModalBody>
            <ModalFooter>
              <Button colorScheme="green" mr={3} onClick={handleNewPlanSubmit}>
                Save
              </Button>
              <Button variant="ghost" onClick={() => setIsModalOpen(false)}>
                Close
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Flex>
    </Box>
  );
};

export default PlanAdmin;
